"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import Image from "next/image";
import { Copy, ExternalLink, ImagePlus, Loader2, Sparkles, X } from "lucide-react";
import { useI18n } from "@/providers/i18n";
import { uploadFile } from "@/lib/upload";
import { useAuth, useUser } from "@clerk/nextjs";
import { useProject } from "@/providers/project";

type MusicShareDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  audioUrl: string;
  defaultTitle?: string;

  existingToken?: string | null;
  existingCoverUrl?: string | null;
  existingEnabled?: boolean | null;

  onSaved?: (token: string) => void;
  onStateChange?: (state: { coverUrl?: string; enabled?: boolean }) => void;
};

export function MusicShareDialog({
  open,
  onOpenChange,
  audioUrl,
  defaultTitle,
  existingToken,
  existingCoverUrl,
  existingEnabled,
  onSaved,
  onStateChange,
}: MusicShareDialogProps) {
  const { t } = useI18n();
  const { getToken } = useAuth();
  const { user } = useUser();
  const project = useProject();

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [title, setTitle] = useState(defaultTitle ?? "");
  const [coverUrl, setCoverUrl] = useState(existingCoverUrl ?? "");
  const [enabled, setEnabled] = useState(existingEnabled ?? true);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle ?? "");
    setCoverUrl(existingCoverUrl ?? "");
    setEnabled(existingEnabled ?? true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingToken]);

  const shareUrl = useMemo(() => {
    if (!existingToken || typeof window === "undefined") return "";
    return `${window.location.origin}/share/music/${existingToken}`;
  }, [existingToken]);

  const handleUploadCover = async (file: File) => {
    if (!user?.id) { toast.error(t("Not logged in")); return; }
    setUploading(true);
    try {
      const token = await getToken({ template: "supabase" });
      if (!token) throw new Error("No auth token");
      const r = await uploadFile(file, "files", {
        token,
        userId: user.id,
        projectId: project?.id ? String(project.id) : undefined,
        assetType: "image",
      });
      setCoverUrl(r.url);
    } catch (e: any) {
      toast.error(String(e?.message || t("Upload failed")));
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateCover = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const res = await fetch("/api/music-share/generate-cover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: title.trim() || defaultTitle }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || t("Generation failed"));
      }
      const { url } = await res.json();
      if (url) setCoverUrl(url);
    } catch (e: any) {
      toast.error(String(e?.message || t("Generation failed")));
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!audioUrl) { toast.error(t("No audio URL")); return; }
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch("/api/music-share", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: existingToken || undefined,
          enabled,
          audioUrl,
          title: title.trim() || undefined,
          coverUrl: coverUrl.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => t("Share failed"));
        throw new Error(msg || t("Share failed"));
      }
      const json = (await res.json().catch(() => ({}))) as any;
      const token = String(json?.token ?? "").trim();
      if (!token) throw new Error(t("Share failed"));

      toast.success(existingToken ? t("Share updated") : t("Share created"));
      onSaved?.(token);
      onStateChange?.({ coverUrl: coverUrl.trim(), enabled });

      const shareLink = `${window.location.origin}/share/music/${token}`;
      try { await navigator.clipboard.writeText(shareLink); toast.success(t("Link copied")); } catch {}
      try { window.open(shareLink, "_blank", "noopener,noreferrer"); } catch {}

      onOpenChange(false);
    } catch (e: any) {
      toast.error(String(e?.message || t("Share failed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-sidebar/95 backdrop-blur-2xl border border-sidebar-border shadow-none rounded-2xl">
        <DialogHeader>
          <DialogTitle>{t("Share music")}</DialogTitle>
          <DialogDescription>{t("Set title and cover, then share your AI song.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">{t("Title")}</div>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("Song title")}
            />
          </div>

          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">{t("Cover image")}</div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUploadCover(file);
                e.target.value = "";
              }}
            />
            <div className="flex items-start min-h-24">
              {coverUrl.trim() ? (
                <div className="relative">
                  <div className="relative size-24 overflow-hidden rounded-lg border border-sidebar-border/30 bg-sidebar-accent/10">
                    <Image src={coverUrl.trim()} alt="cover" fill unoptimized className="object-cover" />
                  </div>
                  <button
                    type="button"
                    className="absolute -right-1.5 -top-1.5 size-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/80 transition-colors"
                    onClick={() => setCoverUrl("")}
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={uploading || generating}
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center justify-center gap-1 size-24 rounded-lg border border-dashed border-sidebar-border/50 bg-sidebar-accent/10 text-muted-foreground hover:border-sidebar-border hover:bg-sidebar-accent/20 transition-colors"
                  >
                    {uploading ? (
                      <Loader2 size={20} className="animate-spin" />
                    ) : (
                      <>
                        <ImagePlus size={18} />
                        <span className="text-[10px]">{t("Upload")}</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={generating || uploading}
                    onClick={handleGenerateCover}
                    className="flex flex-col items-center justify-center gap-1 size-24 rounded-lg border border-dashed border-purple-500/30 bg-purple-500/5 text-purple-400 hover:border-purple-500/60 hover:bg-purple-500/10 transition-colors"
                  >
                    {generating ? (
                      <Loader2 size={20} className="animate-spin" />
                    ) : (
                      <>
                        <Sparkles size={18} />
                        <span className="text-[10px]">{t("AI Generate")}</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {existingToken ? (
            <div className="flex items-center justify-between gap-3 py-2">
              <div className="text-sm">
                <div className="font-medium">{t("Enable share link")}</div>
                <div className="text-muted-foreground">{t("Disable to make the link unavailable")}</div>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>
          ) : (
            <div className="rounded-xl border border-sidebar-border/30 bg-sidebar-accent/10 px-3 py-2 text-xs text-muted-foreground">
              {t("Share link will be enabled automatically after creation.")}
            </div>
          )}

          {shareUrl ? (
            <div className="space-y-2">
              <div className="text-sm font-medium">{t("Share link")}</div>
              <div className="flex items-center gap-2">
                <Input readOnly value={shareUrl} onFocus={(e) => { try { e.currentTarget.select(); } catch {} }} />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(shareUrl); toast.success(t("Copied")); } catch {}
                  }}
                  aria-label={t("Copy link")}
                >
                  <Copy size={14} />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => { try { window.open(shareUrl, "_blank", "noopener,noreferrer"); } catch {} }}
                  aria-label={t("Open")}
                >
                  <ExternalLink size={14} />
                </Button>
              </div>
            </div>
          ) : null}

          <div className="pt-1">
            <Button onClick={handleSave} disabled={!audioUrl || saving || uploading || generating} size="sm">
              {saving ? (
                <Loader2 className="animate-spin" size={16} />
              ) : existingToken ? (
                t("Update share")
              ) : (
                t("Create share")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
